import { isNotNullish } from '@app/helpers/predicate';
import type { CartQuery_magento2_cart_items } from '@app/pages/Cart/__generated__/LegacyCartQueriesTypes';

import type { PricingPageShopProduct_magento2_products_items_Magento2_ConfigurableProduct_variants } from '@app/pages/Pricing/hook/__generated__/PricingPageShopProduct';
import { AlertLayout, AlertMessage, AlertTitle } from '../components/Alert';
import type { ParsedContentFormat } from '../components/ParsedContent';
import ParsedContent from '../components/ParsedContent';
import type { AlertItem } from '../hooks/useAlerts';
import type {
  ShopStoreNoticesQuery,
  ShopStoreNoticesQuery_magento2_cmsBlocks_items,
} from '../pages/Cart/ShopStoreAlerts/__generated__/ShopStoreNoticesQuery';

/*
  Magento advices use the __typename field on returned items to determine their specific implementation of CartItemInterface (distinguish btn software / hardware)
*/
export const isSoftwareCartItem = <T extends CartQuery_magento2_cart_items>(
  item?: T | null,
): boolean => item?.__typename === 'Magento2_VirtualCartItem';

export const isHardwareCartItem = <T extends CartQuery_magento2_cart_items>(
  item?: T | null,
): boolean => item?.__typename === 'Magento2_SimpleCartItem';

// Temporary until we find a better solution from cms side
export function stripHTML(htmlText?: string | null | undefined): string {
  if (!htmlText) return '';

  try {
    return (
      new DOMParser().parseFromString(htmlText, 'text/html')?.firstChild
        ?.textContent ?? ''
    );
  } catch {
    return htmlText;
  }
}

export const cmsBlockToAlertItem = ({
  type,
  format,
  actions,
  cmsBlock: { title, content },
}: CmsBlockToAlertItemArgs): AlertItem => ({
  type,
  actions,
  persist: true,
  layout: content && title ? AlertLayout.Column : AlertLayout.Row,
  content: (
    <>
      {title ? (
        <AlertTitle>
          <ParsedContent format={format}>{title}</ParsedContent>
        </AlertTitle>
      ) : null}
      {content ? (
        <AlertMessage>
          <ParsedContent format={format}>{content}</ParsedContent>
        </AlertMessage>
      ) : null}
    </>
  ),
});

interface CmsBlockToAlertItemArgs extends Pick<AlertItem, 'type' | 'actions'> {
  cmsBlock: ShopStoreNoticesQuery_magento2_cmsBlocks_items;
  format?: ParsedContentFormat;
}

export const getEnabledNotices = (
  data?: Partial<ShopStoreNoticesQuery>,
): ShopStoreNoticesQuery_magento2_cmsBlocks_items[] | undefined => {
  const items = data?.magento2?.cmsBlocks?.items;
  return items
    ?.filter(isNotNullish)
    .filter((notice) => Boolean(notice.content));
};

export const getVariantLabel = (
  variant: PricingPageShopProduct_magento2_products_items_Magento2_ConfigurableProduct_variants,
): string | undefined | null => {
  return variant?.attributes?.find((attr) => attr?.code === 'license_code')
    ?.label;
};
